import React, { useState } from "react";
import { Plus, Inbox } from "lucide-react";
import {
  Button, Field, Input, Textarea, Select, Search, Card, Chip, EmptyState,
  Skeleton, Avatar, ThreeDotMenu, Tabs, Breadcrumb, Pagination, Modal, Drawer,
  Toast, Tooltip,
} from "@/ui";
import ThemeSwitch from "@/theme/ThemeSwitch";

const COLORS = [
  "--bg-shell", "--bg-nav", "--surface", "--surface-hover", "--sunk",
  "--border-strong", "--text", "--text-2", "--text-3",
  "--accent", "--accent-hover", "--good", "--warn", "--bad",
];
const TYPE = [
  ["display", "t-display", "Calm operations console"],
  ["h1", "t-h1", "Today at Tita Baby's"],
  ["h2", "t-h2", "Orders needing attention"],
  ["h3", "t-h3", "Adobo rice bowl"],
  ["body", "t-body", "One product list, one stock count, one order list."],
  ["small", "t-small", "3 days ago · counter"],
  ["label", "t-label", "Money taken today"],
];
const SPACE = [4, 8, 12, 16, 20, 24, 32, 40, 56, 72];
const RADII = [["sm 8", 8], ["md 12", 12], ["lg 16", 16], ["pill", 999]];

function Panel() {
  const [tab, setTab] = useState("a");
  const [page, setPage] = useState(2);
  const [modal, setModal] = useState(false);
  const [drawer, setDrawer] = useState(false);

  return (
    <div style={{ background: "var(--bg-shell)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
      {/* Colour */}
      <p className="t-label muted" style={{ marginBottom: 12 }}>Colour</p>
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))", marginBottom: 32 }}>
        {COLORS.map((c) => (
          <div key={c}>
            <div className="swatch" style={{ background: `var(${c})` }} />
            <div className="t-small mono muted" style={{ marginTop: 6 }}>{c}</div>
          </div>
        ))}
      </div>

      {/* Type */}
      <p className="t-label muted" style={{ marginBottom: 12 }}>Type — Outfit</p>
      <div className="stack" style={{ marginBottom: 32 }}>
        {TYPE.map(([name, cls, sample]) => (
          <div key={name} className="row-between">
            <span className={cls}>{sample}</span>
            <span className="t-small mono muted-3">{name}</span>
          </div>
        ))}
        <div className="t-body num">₱1,234.50 · 0123456789 (tabular)</div>
      </div>

      {/* Spacing + radius */}
      <p className="t-label muted" style={{ marginBottom: 12 }}>Spacing &amp; radius</p>
      <div className="row" style={{ flexWrap: "wrap", marginBottom: 12 }}>
        {SPACE.map((s) => (
          <div key={s} style={{ textAlign: "center" }}>
            <div style={{ width: s, height: s, background: "var(--accent)", borderRadius: 3 }} />
            <div className="t-small muted-3">{s}</div>
          </div>
        ))}
      </div>
      <div className="row" style={{ marginBottom: 32 }}>
        {RADII.map(([lbl, r]) => (
          <div key={lbl} style={{ textAlign: "center" }}>
            <div style={{ width: 56, height: 40, background: "var(--sunk)", border: "1px solid var(--border-strong)", borderRadius: r }} />
            <div className="t-small muted-3" style={{ marginTop: 4 }}>{lbl}</div>
          </div>
        ))}
      </div>

      {/* Buttons */}
      <p className="t-label muted" style={{ marginBottom: 12 }}>Buttons</p>
      <div className="row" style={{ flexWrap: "wrap", marginBottom: 12 }}>
        <Button variant="primary"><Plus size={20} strokeWidth={1.75} />Add product</Button>
        <Button variant="secondary">Cancel</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="danger">Delete</Button>
      </div>
      <div className="row" style={{ flexWrap: "wrap", marginBottom: 32 }}>
        <Button variant="primary" loading>Saving</Button>
        <Button variant="primary" disabled>Disabled</Button>
        <Button variant="secondary" size="sm">Small</Button>
      </div>

      {/* Inputs */}
      <p className="t-label muted" style={{ marginBottom: 12 }}>Inputs</p>
      <div className="grid grid-2" style={{ marginBottom: 12 }}>
        <Field label="Product name" hint="what people see" htmlFor="sg-a"><Input id="sg-a" placeholder="Adobo rice bowl" /></Field>
        <Field label="Category" htmlFor="sg-b"><Select id="sg-b"><option>Rice meals</option><option>Drinks</option></Select></Field>
      </div>
      <div className="grid grid-2" style={{ marginBottom: 12 }}>
        <Field label="Price" error="add a price so people can buy this" htmlFor="sg-c"><Input id="sg-c" placeholder="0.00" /></Field>
        <Field label="Find"><Search placeholder="Search orders" /></Field>
      </div>
      <Field label="Notes" htmlFor="sg-d"><Textarea id="sg-d" placeholder="Anything else" /></Field>

      {/* Cards */}
      <p className="t-label muted" style={{ margin: "20px 0 12px" }}>Cards</p>
      <div className="grid grid-3" style={{ marginBottom: 32 }}>
        <Card><div className="t-h3">Plain card</div><p className="muted t-small">a panel</p></Card>
        <Card interactive footer="tap to open"><div className="t-h3">Interactive</div><p className="muted t-small">hover me</p></Card>
        <Card selected><div className="t-h3">Selected</div><p className="t-small">accent wash</p></Card>
      </div>

      {/* Table */}
      <p className="t-label muted" style={{ marginBottom: 12 }}>Table</p>
      <div className="tbl-wrap" style={{ marginBottom: 32 }}>
        <table className="tbl">
          <thead><tr><th className="sortable">Order</th><th>Way</th><th className="num">Total</th></tr></thead>
          <tbody>
            <tr><td>#041</td><td>counter</td><td className="num">₱540.00</td></tr>
            <tr><td>#042</td><td>chat</td><td className="num">₱1,180.00</td></tr>
            <tr><td>#043</td><td>shop page</td><td className="num">₱95.00</td></tr>
          </tbody>
        </table>
      </div>

      {/* Chips + tabs + pager + crumbs */}
      <p className="t-label muted" style={{ marginBottom: 12 }}>Status &amp; navigation</p>
      <div className="row" style={{ flexWrap: "wrap", marginBottom: 12 }}>
        <Chip>neutral</Chip>
        <Chip tone="good" dot>paid</Chip>
        <Chip tone="warn" dot>waiting</Chip>
        <Chip tone="bad" dot>sold out</Chip>
      </div>
      <div style={{ marginBottom: 12 }}>
        <Tabs value={tab} onChange={setTab} tabs={[{ value: "a", label: "All" }, { value: "b", label: "Chat" }, { value: "c", label: "Counter" }]} />
      </div>
      <div className="row-between" style={{ marginBottom: 32 }}>
        <Breadcrumb items={[{ label: "Orders", href: "#" }, { label: "#041" }]} />
        <Pagination page={page} pages={3} onPage={setPage} />
      </div>

      {/* Empty + skeleton */}
      <p className="t-label muted" style={{ marginBottom: 12 }}>Empty &amp; loading</p>
      <div className="grid grid-2" style={{ marginBottom: 32 }}>
        <EmptyState icon={<Inbox size={20} strokeWidth={1.75} />} title="no orders yet — they show up here as they come in"
          action={<Button variant="primary" size="sm"><Plus size={20} strokeWidth={1.75} />Take an order</Button>} />
        <div className="stack">
          <Skeleton variant="title" />
          <Skeleton variant="line" style={{ width: "90%" }} />
          <Skeleton variant="line" style={{ width: "70%" }} />
          <Skeleton variant="card" />
        </div>
      </div>

      {/* Avatar / menu / overlays / tooltip */}
      <p className="t-label muted" style={{ marginBottom: 12 }}>Bits &amp; overlays</p>
      <div className="row" style={{ flexWrap: "wrap" }}>
        <Avatar name="Tita Baby" />
        <ThreeDotMenu items={[{ label: "Edit" }, { label: "Archive" }]} />
        <Tooltip label="opens a preview first"><Button size="sm">Hover for tip</Button></Tooltip>
        <Button size="sm" onClick={() => setModal(true)}>Open modal</Button>
        <Button size="sm" onClick={() => setDrawer(true)}>Open drawer</Button>
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <Toast tone="good">saved</Toast>
        <Toast tone="bad">could not save — try again</Toast>
      </div>
      <Modal open={modal} title="Give a helper access?" onClose={() => setModal(false)}
        footer={<><Button onClick={() => setModal(false)}>No, go back</Button><Button variant="primary" onClick={() => setModal(false)}>Yes, switch it on</Button></>}>
        <p className="muted">it switches off by itself after 24 hours.</p>
      </Modal>
      <Drawer open={drawer} title="Order #041" onClose={() => setDrawer(false)}>
        <p className="muted">a side panel for details.</p>
      </Drawer>
    </div>
  );
}

export default function StyleGuide() {
  return (
    <div className="shell">
      <div className="app-bg" aria-hidden="true" />
      <div className="page">
        <div className="page-content" style={{ maxWidth: 1280 }}>
          <div className="row-between" style={{ marginBottom: 8 }}>
            <h1 className="t-h1">Reto style guide</h1>
            <ThemeSwitch />
          </div>
          <p className="muted" style={{ marginBottom: 32 }}>
            every token and component, side by side in light and dark. if it is not here, it does not exist.
          </p>
          <div className="grid" style={{ gridTemplateColumns: "1fr", gap: 24 }}>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "start" }}>
              <div data-theme="light"><p className="t-label muted" style={{ marginBottom: 12 }}>Light</p><Panel /></div>
              <div data-theme="dark"><p className="t-label muted" style={{ marginBottom: 12 }}>Dark</p><Panel /></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
